import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import LinkConstants from "../../../constants/LinkConstants.ts";
import { POSTHOG_EVENTS } from "../../../constants/PostHogEvents.ts";
import { getSelectedOrganization } from "../../../helpers/localStorage.ts";
import { useLogEvents } from "../../../hooks/useLogEvents";
import usePostHog from "../../../hooks/usePostHog.tsx";
import { useAppStore } from "../../../hooks/useStores";
import { isMigrationIframe } from "../../../utils/migrationIframe.ts";
import BriaButton, { ButtonTypes } from "../BriaButton/BriaButton";
import CopyToClipboardButton from "../CopyToClipboardButton/CopyToClipboardButton";
import styles from "./CopyApiTokenButton.module.scss";

type Props = {
	buttonType?: ButtonTypes;
	withIcon?: boolean;
	afterCopyAction?: () => void;
	afterCreateAction?: () => void;
	keyType?: string;
	buttonText?: string;
	disabled?: boolean;
	showCopyButton?: boolean;
};

const CopyApiTokenButton = ({
	buttonType = "primaryMedium",
	withIcon = false,
	afterCopyAction,
	afterCreateAction,
	keyType = "staging",
	buttonText = "",
	disabled = false,
	showCopyButton = false,
}: Props) => {
	const { t } = useTranslation("translation", { keyPrefix: "copyApiToken" });
	const { uiStore, appStore } = useAppStore();
	const { logImageGenerationComfyUCopyApiTokenClick, logGetApiKeyButtonClicked } = useLogEvents();
	const apiKey = appStore.apiKeys.find((key) => key.key_type === keyType && key.status === "active");
	const selectedOrg = getSelectedOrganization();
	const { trackEvent } = usePostHog();

	useEffect(() => {
		const loadData = async () => {
			if (!appStore.isError && appStore.apiKeys.length === 0) {
				await appStore.loadApiKeys();
			}
		};
		loadData();
	}, []);

	return (
		<>
			{(selectedOrg || showCopyButton) && !isMigrationIframe() ? (
				<CopyToClipboardButton
					textToCopy={apiKey?.api_token}
					buttonText={buttonText ? buttonText : t("copyButton")}
					buttonType={buttonType}
					hideIcon={!withIcon}
					loading={appStore.isLoading}
					disabled={disabled}
					afterCopyAction={
						afterCopyAction ||
						(() => {
							if (keyType == "comfy") {
								logImageGenerationComfyUCopyApiTokenClick();
							}
						})
					}
				/>
			) : (
				<BriaButton
					buttonType={buttonType}
					onClick={() => {
						if (selectedOrg) {
							logGetApiKeyButtonClicked();
							trackEvent(POSTHOG_EVENTS.GET_API_KEY_BUTTON_CLICKED);
							window.open(LinkConstants.BRIA_API_KEYS_PLATFORM_PAGE, "_blank");
						} else {
							uiStore.showDialog("CreateOrganizationDialog");
							afterCreateAction?.();
						}
					}}
					className={styles.buttonText}
					loading={appStore.isLoading}
				>
					{t("getApiToken")}
				</BriaButton>
			)}
		</>
	);
};

export default observer(CopyApiTokenButton);
